<template>
  <div class="editor-wrapper">
    <p class="token-used">{{ $i18n.t('tokens_used') }}: {{ tokenUsed }}</p>
    <template>
      <el-tabs type="card" v-model="tab" v-if="!compact && id" lazy="true">
        <el-tab-pane name="code" :label="$i18n.t('code')">
          <expression-editor
            :completionSources="sources"
            :assistant="assistant"
            :showTools="true"
            :disableSyntaxCheck="disableSyntaxCheck"
            :name="id"
            @ready="onReady"
            :value="internalCode"
            :options="cmOptions"
            @input="cmInput"
            @contextmenu.native.prevent="showContextMenu($event)"
          />
        </el-tab-pane>
        <el-tab-pane name="code_review" :label="$i18n.t('ai_code_review')">
          <expression-editor-code-review
            :lang="lang"
            :parentAssistant="assistant"
            :identifier="id"
            @onPracticeCodeApply="applyPracticeCode"
            :code="internalCode"
            :codeMirrorInstance="cminstance"
            @onGenerateFinished="onReviewGenerateFinished"
          />
        </el-tab-pane>
      </el-tabs>
      <expression-editor
        v-else
        :name="id"
        :completionSources="sources"
        :assistant="assistant"
        :showTools="true"
        :disableMinHeight="compact"
        :disableSyntaxCheck="disableSyntaxCheck"
        :value="internalCode"
        :options="cmOptions"
        @ready="onReady"
        @input="cmInput"
        @contextmenu.native.prevent="showContextMenu($event)"
      />
    </template>
    <ask-a-i
      :onAsk="generateCode"
      :componentStyle="contextMenuStyle"
      :visible="contextMenuVisible"
      @onChange="(value) => (this.prompt = value)"
      :prompt="prompt"
    />
  </div>
</template>

<script>
import ExpressionEditor from './ExpressionEditor'
import AskAI from '../ai/AskAI.vue'
import AiMixin from '../../utils/mixin-ai'
import { AI_ASSISTANTS, EDITOR_MODES } from '../../consts'
import ExpressionEditorCodeReview from './ExpressionEditorCodeReview.vue'
import { get } from 'lodash'

/**
 * @class CodeEditor
 *
 * @description This is a wrapper for CodeEditor component
 */
export default {
  name: 'CodeEditor',
  components: {
    ExpressionEditorCodeReview,
    ExpressionEditor,
    AskAI,
  },
  props: {
    sources: {
      type: Object,
      default: () => {},
    },
    disableSyntaxCheck: {
      type: Boolean,
      default: false,
    },
    lang: {
      type: String,
      default: 'text/javascript',
    },
    emitOnChange: {
      type: Boolean,
      default: false,
    },
    assistant: {
      type: String,
      default: AI_ASSISTANTS.DEFAULT_CODE_WRITER,
    },
    id: {
      type: String,
    },
    code: {
      type: String,
      default: '',
    },
    modalId: {
      type: String,
    },
    compact: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      threadId: null,
      tab: 'code',
      internalCode: this.code,
      key: 0,
      cminstance: null,
      cmOptions: {
        tabSize: 4,
        mode: this.lang,
        lineNumbers: true,
        line: true,
        foldGutter: true,
        gutters: ['CodeMirror-linenumbers', 'CodeMirror-foldgutter'],
        extraKeys: {
          Tab: 'autocomplete',
        },
        styleActiveLine: true,
        matchBrackets: true,
        autoCloseBrackets: true,
        showTrailingSpace: true,
        autoCloseTags: true,
        showHint: true,
        matchTags: { bothTags: true },
        theme: 'material',
        lint: true,
      },
      prompt: '',
      isGenerating: false,
      tokenUsed: 0,
      contextMenuVisible: false,
      contextMenuStyle: {
        position: 'absolute',
        top: '0px',
        left: '0px',
      },
      additionalCreations: null,
    }
  },
  mixins: [AiMixin],
  computed: {
    codeLangPromp() {
      switch (this.lang) {
        case EDITOR_MODES.VUE:
          return 'vue2'
        case EDITOR_MODES.CSS:
          return 'css'
        case EDITOR_MODES.JAVASCRIPT:
          return 'javascript'
        case EDITOR_MODES.HTML_MIXED:
          return 'vue2 template'
      }
    },
    isModalOpen() {
      return this.$store.getters.isModalOpen(this.modalId)
    },
  },
  methods: {
    getSource(source, defaultValue = []) {
      return get(this.sources, source, defaultValue)
    },
    async getAIProperties() {
      this.hideContextMenu()
      const selection = this.cminstance.getSelection()
      const cursor = this.cminstance.getCursor()
      const isExpressionHelper = this.assistant === AI_ASSISTANTS.EXPRESSION_HELPER
      const currentCode = get(this, 'internalCode', '') || ''

      let textUpToCursor = ''
      const maxLines = cursor.line + 1
      for (let i = 0; i < maxLines; i++) {
        const line = this.cminstance.getLine(i)
        if (line) textUpToCursor += line + '\n'
      }

      const properties = {
        prompt: '',
        assistant: this.assistant,
        fromScratch: currentCode.length === 0,
        hasSelection: selection?.length > 0,
        type: null,
        data: null,
      }

      const getPrompt = await this.$client.execute({
        operationId: 'Ai_getPrompt',
        parameters: {
          assistantName: this.assistant,
          params: JSON.stringify({
            hasSelection: properties.hasSelection,
            isExpressionHelper,
            fromScratch: properties.fromScratch,
            prompt: this.prompt,
            codeLangPromp: this.codeLangPromp,
            formFields: this.getSource('formFields').map((fields) => {
              return `(${fields?.value},db_type: ${fields?.type})`
            }),
            instance: this.getSource('instanceProperties'),
            isFormSave: this.getSource('isFormSave'),
            currentCode,
            selection,
            textUpToCursor,
          }),
        },
      })

      if (getPrompt.data) properties.prompt = JSON.parse(getPrompt.data)
      if (!properties.fromScratch) {
        if (properties.hasSelection) {
          properties.type = 'rewrite'
          properties.data = selection
        } else {
          properties.type = 'completion'
          properties.data = textUpToCursor
        }
      } else {
        properties.type = 'scratch'
      }

      return properties
    },
    formatCode() {
      const CodeMirror = this.cminstance
      var totalLines = CodeMirror.lineCount()
      CodeMirror.operation(function () {
        for (var i = 0; i < totalLines; i++) {
          CodeMirror.indentLine(i, 'smart')
        }
      })
    },
    applyPracticeCode(newCode) {
      this.cminstance.setValue(newCode)
      this.formatCode()
      this.$notify({
        title: this.$i18n.t('code_applied'),
        text: this.$i18n.t('practice_successfully_applied'),
        type: 'success',
      })
    },
    onReviewGenerateFinished(tokens) {
      this.tokenUsed += tokens
    },
    async generateCode() {
      const properties = await this.getAIProperties()
      const params = {
        assistant: properties.assistant,
        message: properties.prompt,
        threadId: this.threadId,
      }
      let lastSetData = null
      this.isGenerating = true
      if (!this.prompt?.length) return

      const stream = this.stream(lastSetData, properties, this.internalCode)
      this.$client.streamEvent(
        '/api/Ais/chat',
        params,
        (data) => {
          stream.onMessage(data)
        },
        (data) => {
          stream.onEnd(data)
        },
        (error) => {
          stream.onError(error)
        }
      )
    },
    stream(lastSetData, properties, internalCode) {
      const that = this
      return {
        onMessage(data) {
          switch (properties.type) {
            case 'scratch':
              that.internalCode = data
              break
            case 'completion':
              const before = properties.data
              const after = internalCode.split(properties.data)[1]
              that.internalCode = (before || '') + data + (after || '')
              break
            case 'rewrite':
              if (that.internalCode.includes(properties.data) && data !== '' && data !== 'javascript') {
                const occurrence = that.countOccurrences(that.internalCode, data)
                if (occurrence <= 1) that.internalCode = that.internalCode.replace(properties.data, data)
                lastSetData = data
              } else {
                that.internalCode = that.internalCode.replace(lastSetData, data)
                lastSetData = data
              }
              break
            default:
              console.error('Message type not supported.')
          }
        },
        onEnd(data) {
          that.tokenUsed += Number(data.split(',')[1])
          that.isGenerating = false
          that.formatCode()
        },
        onError(error) {
          that.isGenerating = false
          that.$notify({
            title: error,
            type: 'error',
          })
        },
      }
    },
    countOccurrences(text, searchText) {
      const regex = new RegExp(searchText, 'g')
      const matches = text.match(regex)
      return matches ? matches.length : 0
    },
    onReady(cminstance) {
      this.cminstance = cminstance
      this.cminstance.focus()
    },
    cmInput(newCode) {
      this.internalCode = newCode
      if (this.emitOnChange) this.$emit('code-change', { value: newCode })
    },
    showContextMenu(event) {
      this.contextMenuStyle = {
        top: `${event.clientY}px`,
        left: `${event.clientX}px`,
        position: 'fixed',
      }
      this.contextMenuVisible = true
      document.addEventListener('click', this.hideContextMenu)
    },
    hideContextMenu() {
      this.contextMenuVisible = false
      document.removeEventListener('click', this.hideContextMenu)
    },
  },
  watch: {
    isModalOpen: {
      handler(newVal) {
        if (!newVal) {
          this.$emit('code-change', { value: this.internalCode })
        } else {
          this.internalCode = this.code
        }
      },
      deep: true,
    },
  },
  beforeDestroy() {
    if (!this.emitOnChange) this.$emit('code-change', { value: this.internalCode })
    document.removeEventListener('click', this.hideContextMenu)
  },
  async mounted() {
    this.threadId = await this.createThread()
  },
}
</script>

<style lang="scss">
.editor-wrapper .el-tabs__nav.is-top {
  border: none;
  border-left: 1px solid #d6d8de;
  border-right: 1px solid #d6d8de;
  border-top: 1px solid #d6d8de;
  border-radius: 0.5em 0.5em 0 0;
}

.editor-wrapper .el-tabs__header {
  margin-bottom: 0;
}

.editor-wrapper .el-tabs__content {
  border: 1px solid #d6d8de;
  border-radius: 0.5em;
  border-top-left-radius: 0em;
  padding: 0.25em;
  min-height: $code-editor-height;
}

.editor-wrapper .el-tab-pane {
  overflow-y: auto;
  min-height: $code-editor-height;
  border: 2px dashed #d6d8de;
  border-radius: 0.3em;
}

.editor-wrapper .el-tabs--card > .el-tabs__header .el-tabs__item.is-active {
  background: #270e6614;
}

.token-used {
  text-align: end;
  opacity: 0.8;
}
</style>
